import * as THREE from 'three';
import { Inventory } from './inventory';
import { nearestOnRay } from './aim';

// Lootable container box. When a node is depleted (or, later, a chest/corpse), its remaining
// contents go into one of these transparent boxes rather than scattering items (spec sections
// 6 and 7). Walk up and interact to loot it.
export class LootContainer {
  readonly mesh: THREE.Group;
  readonly pos: THREE.Vector3;
  readonly contents: Inventory;

  constructor(pos: THREE.Vector3, contents: Inventory) {
    this.pos = pos.clone();
    this.contents = contents;
    this.mesh = buildContainerMesh();
    this.mesh.position.set(this.pos.x, 0.45, this.pos.z);
    this.mesh.traverse((o) => { o.userData.container = this; }); // backref for aim-targeting
  }
}

export class ContainerManager {
  readonly group = new THREE.Group();
  private readonly list: LootContainer[] = [];

  spawn(pos: THREE.Vector3, contents: Inventory): LootContainer {
    const c = new LootContainer(pos, contents);
    this.list.push(c);
    this.group.add(c.mesh);
    return c;
  }

  aimed(raycaster: THREE.Raycaster): LootContainer | null {
    for (const h of raycaster.intersectObject(this.group, true)) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.container) return o.userData.container as LootContainer; o = o.parent; }
    }
    return nearestOnRay(raycaster, this.list.map((c) => ({ x: c.pos.x, y: c.pos.y + 0.45, z: c.pos.z, rec: c })), 0.45);
  }

  // Closest container within horizontal range of the point, or null.
  nearest(p: THREE.Vector3, range: number): LootContainer | null {
    let best: LootContainer | null = null;
    let bestSq = range * range;
    for (const c of this.list) {
      const dx = c.pos.x - p.x;
      const dz = c.pos.z - p.z;
      const sq = dx * dx + dz * dz;
      if (sq <= bestSq) {
        bestSq = sq;
        best = c;
      }
    }
    return best;
  }

  remove(c: LootContainer): void {
    const i = this.list.indexOf(c);
    if (i < 0) return;
    this.list.splice(i, 1);
    this.group.remove(c.mesh);
  }
}

function buildContainerMesh(): THREE.Group {
  const g = new THREE.Group();
  const size = 0.8;
  const box = new THREE.Mesh(
    new THREE.BoxGeometry(size, size, size),
    new THREE.MeshBasicMaterial({ color: 0x5fd0e0, transparent: true, opacity: 0.32, depthWrite: false }),
  );
  g.add(box);
  const edges = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.BoxGeometry(size, size, size)),
    new THREE.LineBasicMaterial({ color: 0x9fe9f5 }),
  );
  g.add(edges);
  return g;
}
